'use client'

import { FormikDateInput } from '@/components/inputs/formik-date-input'
import { FormikTextInput } from '@/components/inputs/formik-text-input'
import { Button, Grid, Typography } from '@mui/material'
import { ErrorMessage, FastField, Form, Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'

export interface IBookingFormDto {
  employeeName: string
  employeeEmail: string
  employeePhone: string
  employeeBirthDate: string
  accept: boolean
}

interface IBookingFormProps {
  handleSubmit: (values: IBookingFormDto, helpers: FormikHelpers<IBookingFormDto>) => void
}

const emailRegExp =
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/

const phoneRegExp =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/

const validationSchema = yup.object({
  employeeName: yup.string().required('Kötelező mező'),
  employeeEmail: yup
    .string()
    .matches(emailRegExp, 'Helytelen formátum, kérlek így add meg: john@email.com')
    .required('Kötelező mező'),
  employeePhone: yup
    .string()
    .matches(phoneRegExp, 'Helytelen formátum, kérlek így add meg: +36201234567')
    .required('Kötelező mező'),
  employeeBirthDate: yup
    .date()
    .typeError('Helytelen dátum')
    .min('1900-01-01', 'Helytelen dátum')
    .max(new Date(), 'Helytelen dátum')
    .required('Kötelező mező'),
  socialSecurityNumber: yup
    .string()
    .test('len', 'Helytelen formátum, a TAJ szám 9 karakterből áll', (val) => val?.length === 9)
    .required('Kötelező mező!'),
  accept: yup.boolean().oneOf([true], 'Kérjük fogadd el az adatkezelési irányelveket'),
})

const initialValues = {
  employeeName: '',
  employeeEmail: '',
  employeePhone: '',
  employeeBirthDate: '',
  accept: false,
}

export const BookingForm = (props: IBookingFormProps) => {
  const { handleSubmit } = props

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: 3 }}>
              <>
                <FastField label="Név" name="employeeName" component={FormikTextInput} />
              </>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: 3 }}>
              <FastField
                label="E-mail cím"
                name="employeeEmail"
                component={FormikTextInput}
                placeholder="Kérjük a személyes email címedet add meg"
                tooltipContent="A vizsgálatok során elkészült személyes dokumentumokat emailben továbbítjuk, így javasoljuk a személyes email cím használatát. Továbbá a céges levelezőrendszerek, nem minden esetben kezelik jól a csatolmánnyal rendelkező leveleket."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: 3 }}>
              <FastField label="Telefonszám" name="employeePhone" component={FormikTextInput} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: 3 }}>
              <FastField
                label="Születési dátum"
                name="employeeBirthDate"
                component={FormikDateInput}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: 3 }}>
              <FastField label="TAJ szám" name="socialSecurityNumber" component={FormikTextInput} />
            </Grid>
            <Grid>
              <Typography marginTop={6}>
                <FastField type="checkbox" name="accept" />
                &nbsp;Az&nbsp;
                <a
                  href="https://vhmedical.hu/VHMedical_adatkezel%C3%A9s.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  Adatkezelési tájékoztatót
                </a>
                &nbsp;megismertem, rendelkezéseit elfogadom, egyúttal önkéntes és kifejezett
                hozzájárulásomat adom személyes adataim Adatkezelő által történő kezeléséhez.
              </Typography>
              <ErrorMessage name="accept" component="div" className="formError" />
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ paddingTop: 4, textAlign: 'center' }}>
              <Button loading={isSubmitting} type="submit" variant="contained" size="large">
                Foglalás beküldése
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
